import React from "react";

// External
import styled, { css } from "styled-components";
import { FiChevronLeft } from "react-icons/fi";
import { navigate } from "gatsby";

// Internal
import useMediaQuery from "../../hooks/use-media-query";
import IconButton from "../../components/form/IconButton";
import { Flex } from "../../styles/global-styles";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import {
  fontFamily,
  colors,
  breakpoints,
  getProjectTheme,
  sectionRhythm,
} from "../../../theme";
import { PROJECT_HARMINDRA_INTERNATIONAL } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import Testimonials from "../reviews-section/Testimonials";

const projectTheme = getProjectTheme("harmindra-international");

// Styles

const BlogContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const BlogTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;
const Video = styled.video`
  //   width: 100%;
  height: 102%;
  border-radius: 6px !important;
  transform: scale(1.01);
  // box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.1);
  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  width: ${(props) => (props.isMobileCover ? "96%" : "100%")};
  border: 4px solid white;
  border-radius: 6px !important;
  overflow: hidden;
  box-sizing: border-box;
  background-image: linear-gradient(180deg, ${colors.white}, ${colors.white});
  box-shadow: 17px 17px 48px 0 rgb(0 0 0 / 12%);
  margin-top: 1.5rem;
`;

const BlogContentContainer = styled.div`
  ${caseStudyBodyStyles}
`;
const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  margin-bottom: 0;
`;

const OverviewItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 1000px) {
    flex-direction: row;
  }
`;

const OverviewItem = styled.div`
  margin-bottom: 1rem;

  ${(props) =>
    props.isSeventyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 70%;
      }
    `}
  ${(props) =>
    props.isThirtyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 30%;
      }
    `}
`;
const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;
const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;

  a,
  &[href] {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

const GalleryContentContainer = styled.div`
  margin-block: 2rem;
`;

const GalleryContentItem = styled.div`
  margin-block: 2rem;
  position: relative;
`;

const VideoCaption = styled.span`
  font-size: 0.8rem;
  text-align: center;
  width: 100%;
  display: inline-block;
  margin-top: 0.8rem;
`;

const FigmaIframe = styled.iframe`
  width: 100%;
  height: 300px;
  margin-top: 1.5rem;
  @media only screen and (min-width: ${breakpoints.tablet}) {
    height: 540px;
  }
`;

const HarmindraInternationalProductCatalogue = () => {
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);

  const goBack = () => {
    navigate(-1);
  };

  const randomData = {
    date: new Date(),
    title:
      "Establishing online identity for a manufacturing firm of India through product catalog website",
    clientName: "Harmindra International",
    projectName: "Harmindra International",
    role: "React developer, UI/UX designer, Copywriter",
    year: "2021",
    cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039786/projects/harmindra-international/harmindra-international__landing_u4zcsw.mp4",
    mobile_cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039755/projects/harmindra-international/harmindra-international__landing--mobile_cpfkdx.mp4",
    clientCountry: "India",
    servicesProvided: "Front-end development, UI/UX design, Copywriting, CMS",
    industry: "Manufacturing",
    techStack: [
      "TypeScript",
      "Gatsby (React)",
      "Material UI",
      "Contentful CMS",
      "Figma",
    ],
    projectLink: "",
    clientReviews: [
      {
        name: "Rajinder Singh",
        position: "Founder & CEO",
        company: "Harmindra International",
        profilePic: "elisa-condexo.jpeg",
        text: "It was our pleasure to work with Parmeet. He worked on creating the online identity of our business. He got not just the development of the website done but got the copywriting and product photography work done as well. The website created by him is fast and responsive. It has helped us land many new international clients. I would strongly recommend hiring him",
      },
    ],
  };

  const mediaLinks = {
    cms: "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039763/projects/harmindra-international/harmindra-international__cms_nishph.mp4",
    about:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039801/projects/harmindra-international/harmindra-international__about_d11ufk.mp4",
    product:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039731/projects/harmindra-international/harmindra-international__product_br5oac.mp4",
    categories:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039727/projects/harmindra-international/harmindra-international__categories_t5vacl.mp4",
    photoShoot:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039720/projects/harmindra-international/harmindra-internatinoal__photoshoot_sr6ugw.mp4",
  };

  return (
    <>
      <BlogContainer>
        {isMobile && (
          <Flex spaceBetween>
            <IconButton
              bgColor="#bbb"
              iconColor="#000"
              style={{ marginLeft: "-12px" }}
              onClick={goBack}
              ariaLabel="Go back"
            >
              <FiChevronLeft />
            </IconButton>
            {/* <SocialMediaButtons shareLinks={shareLinks} isMobile={true} /> */}
          </Flex>
        )}
        <ContainerScroll
          titleComponent={<BlogTitle>{randomData.title}</BlogTitle>}
          role={randomData.role}
        >
          <ProjectHero
            src={PROJECT_HARMINDRA_INTERNATIONAL.media.hero}
            alt={randomData.title}
          />
        </ContainerScroll>
        <OverviewSection>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{randomData.clientName}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{randomData.clientCountry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{randomData.servicesProvided}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{randomData.industry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          {isMobile && (
            <OverviewItem isHundredPercent>
              <OverviewLabel>Year</OverviewLabel>
              <OverviewValue>{randomData.year}</OverviewValue>
            </OverviewItem>
          )}
          <OverviewItem isHundredPercent>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{randomData.techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {randomData.projectLink ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={randomData.projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {randomData.projectLink}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>
        <BlogContentContainer>
          <section>
            <h4>Challenge</h4>
            <p>
              Harmindra International is a renowned manufacturer in India. Most
              of their clients are from within India only. They wanted to expand
              and sell beyond national boundaries. But they found it hard to
              market and sell their products since they had no online identity
              as of now. Also, they wanted the ability to perform operations
              like adding a product, removing a product, changing a product, etc
              in a no-code manner.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.product} />
            </VideoContainer>
          </section>
          <section>
            <h4>Planning & competition analysis</h4>
            <p>
              The project began with meetings with the management of the company
              to get a better understanding of their project, their industry,
              their target audience, and their competition. Once I had the
              necessary information, I went to analyze the competition's website
              and learn about the strategies they were implementing.
            </p>
          </section>
          <section>
            <h4>Copywriting</h4>
            <p>
              The copy of the website was created to ensure the right words were
              put in the right place, to create a perfect balance of SEO and
              favorable content for the website's audience.
            </p>
          </section>
          <section>
            <h4>Product photography</h4>
            <p>
              Images can make or break the look and feel of the website.
              Unfortunately, the images provided by the client were not of good
              quality. So I went ahead with clicking and editing images of the
              products.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.photoShoot} />
            </VideoContainer>
          </section>
          <section>
            <h4>CMS</h4>
            <p>
              Contentful CMS was used for storing all the products and
              categories, allowing the client to easily edit and update data.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.cms} />
            </VideoContainer>
          </section>
          <section>
            <h4>UI/UX design</h4>
            <p>
              As per the client's input and our research, the target audience of
              the website was mostly middle-aged people. Hence, I went ahead
              with designing a minimal-looking UI for the website. The primary
              color of the website was kept blue as compared to yellow by most
              of the competitors since yellow would have been a little too harsh
              for our target audience's eyes. Most of the target audience used
              mobile devices to access the internet, so a mobile-first approach
              was followed in the designs.
            </p>
            <FigmaIframe
              src="https://www.figma.com/embed?embed_host=share&url=https%3A%2F%2Fwww.figma.com%2Ffile%2FrZms8sd6nu8SJMYnZFGbF1%2FHarminder-International%3Fnode-id%3D0%253A1"
              allowfullscreen
            ></FigmaIframe>
          </section>
          <section>
            <h4>Development</h4>
            <p>
              Gatsby was chosen as the front-end framework since the speed of
              the website was the client's highest priority. Gatsby's official
              starter was used as the boilerplate code for React to keep the
              codebase as light as possible and packages as the demand arises.
              Using TypeScript for creating components ensures more robust and
              reliable code. The forms in the website used the power of Netlify
              Forms & React hook forms for accepting inputs. Zustand and Immer
              were used for state management needs.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.about} />
            </VideoContainer>
          </section>
          <section>
            <h4>Outcome</h4>
            <p>
              The website was released for a beta testing program initially, and
              after clients were happy with the outcome, it was pushed to the
              production server, and the result was a blazing fast website that
              made sure no lead was missed due to that never-ending loader on
              the screen. Google Analytics was installed in the web application
              which helped them greatly as they were able to have an even better
              idea of their target audience and the products they view the most,
              Thus helping them target better and increase their sales.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.categories} />
            </VideoContainer>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials
              showMore={false}
              reviews={PROJECT_HARMINDRA_INTERNATIONAL.client.reviews}
            />
          </section>
          {/*     <br />
        <br />
        <br />
        <br />
        <br />
        <h4>Gallery</h4>
        <GalleryContentContainer>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
            <VideoCaption>My payments</VideoCaption>
          </GalleryContentItem>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
          </GalleryContentItem>
        </GalleryContentContainer> */}
        </BlogContentContainer>
      </BlogContainer>
      <ContactMeSection />
    </>
  );
};

export default HarmindraInternationalProductCatalogue;

/* DONE: Header Component */
/* TODO: Cover Image */
/* TODO: Project overview Section */
/* TODO: Challenge Section */
/* TODO: Process Section */
/* TODO: Outcome Section */
/* TODO: Client review Section */
/* TODO: Gallery Section */
/* TODO: Add link to right of project name */
/* TODO: Add icons for Role Section */
